import { useGlobalAction } from "@gadgetinc/react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { Icon } from "@shopify/polaris";
import { message } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ReactFlowProvider, useEdgesState, useNodesState } from "reactflow";
import { api } from "../api";
import ErrorModal from "./ErrorModal";
import {
  getAutomationStatus,
  getAutomationTypeIcon,
} from "../externalPages/automations/helpers/functions";
import { generateInitialNodesAndEdges } from "../functions/generateInitialNodesAndEdges";
import { saveScenarioFlow } from "../functions/saveScenarioFlow";
import { CustomerContext } from "../providers";
import ArchiveModal from "./ArchiveModal";
import { useLoadScenario } from "./contexts/LoadScenarioContext";
import FlowEditor from "./FlowEditor";
import LoadingSpinner from "./LoadingSpinner";
import { PageHeading } from "./PageHeading";
import { SendATest } from "./SendATest";
import ShareModal from "./ShareModal";
import { Button } from "./tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "./tailwind/dialog";
import { Input } from "./tailwind/input";

export function LoadScenario({ scenario, campaignId }) {
  const { key } = useLoadScenario();
  return (
    !!scenario && (
      <>
        <LoadNodes key={key} scenario={scenario} campaignId={campaignId} />
      </>
    )
  );
}

function LoadNodes({ scenario, campaignId }) {
  const { user } = useContext(CustomerContext);
  const [result, getFlowNodes] = useGlobalAction(api.getFlowNodes, {
    scenarioId: scenario.id,
  });
  const { data, fetching } = result;

  useEffect(() => {
    if (user?.customerId && scenario?.id) {
      getFlowNodes({
        scenarioId: scenario.id,
      });
    }
  }, [user?.customerId, scenario.id]);

  return !data || fetching ? (
    <LoadingSpinner />
  ) : (
    <LoadEdges
      scenario={scenario}
      dataNodes={data}
      user={user}
      campaignId={campaignId}
    />
  );
}

function LoadEdges({ scenario, dataNodes, user, campaignId }) {
  const [isCleaning, setIsCleaning] = useState(true);
  const [edges, setEdges] = useState([]);
  const [isLoadingEdges, setIsLoadingEdges] = useState(false);

  const fetchFlowEdges = async (setEdges) => {
    const result = await api.flowEdge.findMany({
      first: 250,
      filter: {
        scenarioId: { equals: scenario.id },
        sourceNodeId: { isSet: true },
        targetNodeId: { isSet: true },
      },
      select: {
        id: true,
        isSourceTrueCondition: true,
        isSourceFalseCondition: true,
        sourceNode: { id: true },
        targetNode: { id: true },
      },
    });
    setEdges(result);
  };

  const loadEdgesWithCleanup = async () => {
    try {
      setIsLoadingEdges(true);
      await fetchFlowEdges(setEdges);
    } catch (error) {
      console.log("Error fetching flow edges:", error);
      await api.cleanCorruptedFlowEdges({
        scenarioId: scenario.id,
        customerId: user.customerId,
      });
      await fetchFlowEdges(setEdges);
    } finally {
      setIsCleaning(false);
      setIsLoadingEdges(false);
    }
  };

  useEffect(() => {
    if (scenario?.id) {
      loadEdgesWithCleanup();
    }
  }, [scenario?.id]);

  if (isCleaning || isLoadingEdges) {
    return <LoadingSpinner />;
  }

  return (
    <ReactFlowProvider>
      <PageMarkup
        scenario={scenario}
        dataNodes={dataNodes}
        dataEdges={edges}
        user={user}
        campaignId={campaignId}
      />
    </ReactFlowProvider>
  );
}

function PageMarkup({ scenario, dataNodes, dataEdges, user, campaignId }) {
  const navigate = useNavigate();
  // Flow initialisation
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const [initialNodes, initialEdges] = generateInitialNodesAndEdges(
    dataNodes,
    dataEdges,
    setSelectedNodeId,
    scenario?.triggerCount ?? scenario?.campaign?.read ?? 0,
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [modifications, setModifications] = useState(false);

  console.groupCollapsed("Load scenario Page markup (nodes / edges)");
  console.table(nodes);
  console.table(edges);
  console.groupEnd();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [nodes, edges]);

  //useAutoLayout(nodes.find((n) => n?.id === selectedNodeId)?.height);

  const [modalUrl, setModalUrl] = useState(null);
  const [isModalSaveOpen, setIsModalSaveOpen] = useState(false);
  const [isModalShareOpen, setIsModalShareOpen] = useState(false);
  const [isModalTestOpen, setIsModalTestOpen] = useState(false);
  const [isModalDuplicateOpen, setIsModalDuplicateOpen] = useState(false);

  const [isEditingInput, setIsEditingInput] = useState(false);
  const [campaignName, setCampaignName] = useState(scenario.name);
  const [isRenameLoading, setIsRenameLoading] = useState(false);

  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const isAutomation = !!scenario?.campaign?.automationType;
  const [isAutomationActive, setIsAutomationActive] = useState(
    !["api", "klaviyo", "splio"].includes(scenario?.campaign?.automationType)
      ? getAutomationStatus(scenario?.campaign)?.isAutomationActive
      : scenario?.campaign?.isAutomationActive,
  );
  const [isChatbotActive, setIsChatbotActive] = useState(
    scenario?.status === "active",
  );

  const [isModalArchiveOpen, setIsModalArchiveOpen] = useState(false);

  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditingInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditingInput]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      setCampaignName(e.target.value);
      handleRename(campaignName);
    }
  };

  const handleRename = async () => {
    setIsRenameLoading(true);
    if (scenario.type === "chatbot") {
      await api.scenario.update(scenario.id, {
        name: campaignName,
      });
    } else {
      await api.campaign.update(scenario.campaignId, {
        name: campaignName,
      });
      await api.scenario.update(scenario.id, {
        name: campaignName,
      });
    }
    setIsEditingInput(false);
    setIsRenameLoading(false);
  };

  const handleShareClick = () => {
    setIsModalShareOpen(true);
  };

  const handleArchiverClick = () => {
    setIsModalArchiveOpen(true);
  };

  const handleDuplicateConfirmation = async () => {
    setIsDuplicateLoading(true);
    console.log("duplicate");
    try {
      if (scenario?.type === "chatbot") {
        const newChatbot = await api.duplicateChatbotNodesAndEdges({
          oldScenarioId: scenario?.id,
        });
        navigate(`/chatbots/edit/${newChatbot.id}`);
      } else {
        const newCampaign = await api.duplicateCampaignNodesAndEdges({
          oldCampaignId: scenario?.campaignId,
        });
        navigate(
          `/${isAutomation ? "automations" : "campaigns"}/edit/${newCampaign.id}`,
        );
      }
    } catch {
      setIsModalDuplicateOpen(false);
      setIsErrorModalOpen(true);
    } finally {
      setIsDuplicateLoading(false);
    }
  };

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleAutomationStatus = async () => {
    setIsAutomationActive(!isAutomationActive);
    if (
      !["api", "klaviyo", "splio"].includes(scenario.campaign.automationType)
    ) {
      await api.customer.update(user.customerId, {
        [mappingAutomationTypeWithField[scenario.campaign.automationType]]:
          isAutomationActive ? null : { _link: scenario?.campaign.id },
      });
    }
    await api.campaign.update(scenario?.campaign.id, {
      isAutomationActive: !isAutomationActive,
    });

    if (
      !isAutomationActive &&
      (scenario.campaign.automationType === "abandoned-cart" ||
        scenario.campaign.automationType === "just-abandoned-checkout")
    ) {
      try {
        const flowNode = await api.flowNode.maybeFindFirst({
          filter: {
            scenarioId: {
              equals: scenario.id,
            },
            type: {
              equals: "automation_trigger",
            },
          },
        });
        if (flowNode) {
          api.customer.update(user.customerId, {
            abandonedCartDelayInMinutes: flowNode.abandonedCartDelayInMinutes,
          });
        }
      } catch (e) {
        console.log(e);
      }
    }
    message.success(
      `${scenario.name} is now ${isAutomationActive ? "inactive" : "active"}`,
    );
  };

  const handleChatbotStatus = async () => {
    setIsChatbotActive(!isChatbotActive);

    try {
      await api.scenario.update(scenario.id, {
        status: scenario.status === "active" ? "inactive" : "active",
      });
      if (scenario.rootNode) {
        await api.whatsappTrigger.update(scenario.rootNode.whatsappTrigger.id, {
          status: scenario.status === "active" ? "inactive" : "active",
        });
        if (scenario.chatbotType === "autoreply") {
          await api.customer.update(user.customer.id, {
            automaticReplyNode: {
              _link: scenario.status === "active" ? null : scenario.rootNode.id,
            },
          });
        }
        if (scenario.chatbotType === "whatsapp_order") {
          await api.customer.update(user.customer.id, {
            whatsappOrderNode: {
              _link: scenario.status === "active" ? null : scenario.rootNode.id,
            },
          });
        }
      }
    } finally {
      message.success(
        `${scenario.name} is now ${isChatbotActive ? "inactive" : "active"}`,
      );
    }
  };

  const automationTypeIcon = getAutomationTypeIcon(
    scenario?.campaign?.automationType,
  );

  const mappingAutomationTypeWithField = {
    "abandoned-cart": "abandonedCartCampaign",
    "just-abandoned-checkout": "abandonedCartCampaign",
    "back-in-stock": "backInStockCampaign",
    "order-confirmation": "orderConfirmationCampaign",
    "order-tracking": "orderTrackingCampaign",
    "optin-confirmation": "optinConfirmationCampaign",
  };

  return (
    <>
      <div className="relative" style={{ maxHeight: "50px" }}>
        <PageHeading
          style={{ maxHeight: "50px" }}
          title={
            <>
              <div className="flex">
                {scenario?.campaign?.automationType && (
                  <Icon
                    source={automationTypeIcon}
                    color="base"
                    accessibilityLabel="Status"
                  />
                )}
                <div className="ml-2">{campaignName}</div>
              </div>
            </>
          }
          titleAction={true}
          editAutomation={scenario.type === "automation"}
          dropdownButton={true}
          dropdownItems={
            scenario.type === "chatbot"
              ? [
                  {
                    label: "Archive",
                    onClick: () => handleArchiverClick(scenario.campaignId),
                  },
                  {
                    label: "Duplicate",
                    onClick: () => setIsModalDuplicateOpen(true),
                  },
                ]
              : [
                  {
                    label: "Analytics",
                    onClick: () =>
                      navigate(`/analytics/${scenario.campaignId}`),
                  },
                  {
                    label: "Send a test",
                    onClick: () => setIsModalTestOpen(true),
                  },
                  {
                    label: "Duplicate",
                    onClick: () => setIsModalDuplicateOpen(true),
                  },
                  {
                    label: "Share",
                    onClick: () => handleShareClick(scenario?.campaign),
                  },
                ]
          }
          handleTitleAction={() => {
            setIsEditingInput(true);
          }}
          secondaryAction="Save"
          isSecondaryLoading={isLoading || isSubmitLoading}
          handleSecondaryAction={async (event) => {
            event.preventDefault();
            setIsLoading(true);
            await saveScenarioFlow(
              nodes,
              edges,
              scenario.id,
              campaignId,
              user,
              campaignName,
            );
            setIsEditingInput(false);
            setIsLoading(false);
            setModifications(false);
            message.success(`Flow successfully saved`);
          }}
          {...(scenario.type === "campaign" && {
            handlePrimaryAction: async () => {
              if (modifications) {
                setIsModalSaveOpen(true);
                setModalUrl("/campaigns/send/" + scenario.campaignId);
              } else {
                navigate("/campaigns/send/" + scenario.campaignId);
              }
            },
            primaryAction: "Schedule",
          })}
          {...(scenario.type !== "campaign" && {
            editAutomationOrChatbot:
              scenario.type === "automation" || scenario.type === "chatbot",

            checked:
              scenario?.type === "chatbot"
                ? isChatbotActive
                : isAutomationActive,
            onChange:
              scenario?.type === "chatbot"
                ? () => handleChatbotStatus()
                : () => handleAutomationStatus(),
          })}
        />
        {isEditingInput && (
          <>
            <div className="flex items-center" style={{ maxWidth: "75%" }}>
              <Input
                ref={inputRef}
                className="absolute top-[-60px] z-10 left-[9px] font-title font-bold leading-7 text-gray-900 dark:text-white"
                style={{ maxWidth: "100%", margin: "0", fontSize: "30px" }}
                value={campaignName}
                onChange={(e) => {
                  setCampaignName(e.target.value);
                }}
                autoComplete="off"
                onKeyUp={(e) => {
                  handleKeyPress(e);
                }}
              />
              <Button
                outline
                className="absolute top-[-60px] left-[9px] bg-white h-12 w-12"
                onClick={() => {
                  handleRename();
                }}
                loading={isRenameLoading}
              >
                <CheckIcon className="w-20 h-20" />
              </Button>
            </div>
          </>
        )}
      </div>

      <FlowEditor
        nodes={nodes}
        setNodes={setNodes}
        onNodesChange={onNodesChange}
        edges={edges}
        setEdges={setEdges}
        onEdgesChange={onEdgesChange}
        selectedNodeId={selectedNodeId}
        setSelectedNodeId={setSelectedNodeId}
        modifications={modifications}
        setModifications={setModifications}
        scenario={scenario}
        campaignId={campaignId}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        isSubmitLoading={isSubmitLoading}
        setIsSubmitLoading={setIsSubmitLoading}
      />

      <Dialog
        open={isModalSaveOpen}
        onClose={() => setIsModalSaveOpen(false)}
        key="dialog_save_warning"
      >
        <DialogTitle>Are you sure you want to leave this page?</DialogTitle>
        <DialogBody>
          <p>Any unsaved changes will be lost.</p>
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={() => setIsModalSaveOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={async () => {
              navigate(modalUrl);
            }}
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isModalTestOpen}
        onClose={() => setIsModalTestOpen(false)}
        key="dialog_test"
      >
        <DialogTitle>Send a test</DialogTitle>
        <DialogBody>
          {/* <p>Any unsaved changes will be lost.</p> */}
          <SendATest user={user} />
        </DialogBody>
      </Dialog>

      <ShareModal
        campaign={scenario?.campaign}
        scenarioId={scenario?.id}
        isModalShareOpen={isModalShareOpen}
        setIsModalShareOpen={setIsModalShareOpen}
        isAutomation={isAutomation}
      />
      <ArchiveModal
        customer={user.customer}
        scenario={scenario}
        isModalArchiveOpen={isModalArchiveOpen}
        setIsModalArchiveOpen={setIsModalArchiveOpen}
        pageEdit={true}
      />
      <Dialog
        key="dialog_duplicate"
        open={isModalDuplicateOpen}
        onClose={() => {
          if (!isDuplicateLoading) {
            setIsModalDuplicateOpen(false);
          }
        }}
      >
        <DialogTitle close={!isDuplicateLoading}>
          {`Duplicate this ${scenario?.type}`}
        </DialogTitle>
        <DialogBody className="flex-1 overflow-y-auto scrollbar">
          <p>
            {`Are you sure you want to duplicate the ${scenario?.type} `}
            <b>{scenario?.name}</b>?
          </p>
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={() => setIsModalDuplicateOpen(false)}
            disabled={isDuplicateLoading}
            className="min-w-[100px] h-10"
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={() => handleDuplicateConfirmation()}
            className="min-w-[100px] h-10"
            loading={isDuplicateLoading}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorModal
        open={isErrorModalOpen}
        onClose={handleErrorModalClose}
        errorType={"duplicate"}
      />
    </>
  );
}
